import { type FC } from "react";

import classNames from "classnames";
import { NumberFormatter } from "../../../utils/NumberFormatter";
import { useTranslation } from "react-i18next";

import classes from "./StockIndicator.module.less";

interface StockIndicatorProps {
  className?: string;
  level: StockLevel;
  changeText?: string;
  checkAvailability?: boolean;
}

export enum StockLevel {
  IN_STOCK,
  PARTIAL_STOCK,
  NOT_IN_STOCK,
}

export const stockConfig = {
  [StockLevel.NOT_IN_STOCK]: {
    translationKey: "stockIndicator.missing",
    iconClass: `${classes.icon} ${classes.missing} flex-none`,
  },
  [StockLevel.PARTIAL_STOCK]: {
    translationKey: "stockIndicator.partial",
    iconClass: `${classes.icon} ${classes.partial} flex-none`,
  },
  [StockLevel.IN_STOCK]: {
    translationKey: "stockIndicator.exists",
    iconClass: `${classes.icon} ${classes.exists} flex-none`,
  },
};

export const StockIndicatorIcon = ({ level, className }: { level: StockLevel; className?: string }) => {
  const { iconClass } = stockConfig[level];
  return <div className={classNames(iconClass, className)} />;
};

const StockIndicator: FC<StockIndicatorProps> = (props) => {
  const { level, className, changeText } = props;
  const { t } = useTranslation();

  const { translationKey } = stockConfig[level];
  const cls = classNames(classes.stockIndicator, className);

  return (
    <div className={cls}>
      <StockIndicatorIcon level={level} />
      <span className={classes.text} data-test-id={"stock-indicator"}>
        {changeText ? changeText : t(translationKey)}
      </span>
      {props.checkAvailability && (
        <span className={classes.checkAvailability}>{t("stockIndicator.check.availability")}</span>
      )}
    </div>
  );
};

interface SimpleStockIndicatorProps {
  className?: string;
  inStock?: boolean;
  checkAvailability?: boolean;
}

export const SimpleStockIndicator = ({ className, inStock, checkAvailability }: SimpleStockIndicatorProps) => (
  <StockIndicator
    className={className}
    level={inStock ? StockLevel.IN_STOCK : StockLevel.NOT_IN_STOCK}
    checkAvailability={checkAvailability}
  />
);

interface QuantityStockIndicatorProps {
  className?: string;
  quantity: number;
}

export const QuantityStockIndicator = ({ className, quantity }: QuantityStockIndicatorProps) => {
  const level = quantity > 0 ? StockLevel.IN_STOCK : StockLevel.NOT_IN_STOCK;

  const { t } = useTranslation();
  const label =
    quantity > 0
      ? t("stockIndicator.existsWithQty", { amount: NumberFormatter.getFormatter().format(quantity) })
      : t("stockIndicator.missing");

  return <StockIndicator className={className} level={level} changeText={label} />;
};

export function getStockLevel(availableStock: number = 0, requestedQty: number = 0) {
  if (!availableStock) {
    return StockLevel.NOT_IN_STOCK;
  }
  if (availableStock >= requestedQty) {
    return StockLevel.IN_STOCK;
  } else {
    return StockLevel.PARTIAL_STOCK;
  }
}

export default StockIndicator;
